import { formatDistance } from 'date-fns'
import { enUS, ko, type Locale as DateFnsLocale } from 'date-fns/locale'
import { formatInTimeZone } from 'date-fns-tz'
import type { Locale } from '@/shared/config/schema'

const DATE_FNS_LOCALES: Record<Locale, DateFnsLocale> = { ko, en: enUS }

export const DEFAULT_DATE_TIME_PATTERN = 'yyyy-MM-dd HH:mm:ss'

export interface FormatContext {
  locale: Locale
  /** IANA timezone timestamps are shown in: the device's, see `deviceTimeZone()`. */
  timeZone: string
}

type DateInput = string | number | Date

function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
    return true
  } catch {
    return false
  }
}

/** The reported timezone when it can be used, else `UTC`. */
export function usableTimeZone(reported: string | undefined): string {
  return reported && isValidTimeZone(reported) ? reported : 'UTC'
}

/** The IANA timezone of this device, as the browser reports it; `UTC` when it reports none. */
export function deviceTimeZone(): string {
  return usableTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
}

/** Formats a UTC timestamp in the given timezone, independent of the runtime's own one. */
export function formatDateTime(
  value: DateInput,
  { locale, timeZone }: FormatContext,
  pattern: string = DEFAULT_DATE_TIME_PATTERN,
): string {
  return formatInTimeZone(value, timeZone, pattern, { locale: DATE_FNS_LOCALES[locale] })
}

/** "5 minutes ago" in the active language. `now` is injectable. */
export function formatRelative(
  value: DateInput,
  { locale }: Pick<FormatContext, 'locale'>,
  now: Date = new Date(),
): string {
  return formatDistance(new Date(value), now, { addSuffix: true, locale: DATE_FNS_LOCALES[locale] })
}

export function formatNumber(
  value: number,
  { locale }: Pick<FormatContext, 'locale'>,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(value)
}

/** `ratio` is 0..1. */
export function formatPercent(
  ratio: number,
  { locale }: Pick<FormatContext, 'locale'>,
  maximumFractionDigits = 1,
): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits }).format(ratio)
}
